import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { manualTransactionContract } from "@common/contracts/manual-transaction.contract";
import { UpdateManualTransactionBody } from "@common/schemas/domains/transactions/manual-transaction.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { invalidateTransactionDetailsQueries } from "./useTransactionDetailsQuery";

const manualTransactionContractClient = getBackendAPIClient(
    manualTransactionContract,
);

interface UpdateManualTransactionVariables {
    entityId: number;
    transactionId: number;
    update: UpdateManualTransactionBody;
}

export function useUpdateManualTransactionMutation() {
    const queryClient = useQueryClient();
    const { toast, error } = useToaster();

    return useMutation({
        mutationFn: async ({
            entityId,
            transactionId,
            update,
        }: UpdateManualTransactionVariables) =>
            await manualTransactionContractClient.updateManualTransaction({
                params: { entityId, transactionId },
                body: update,
            }),
        onSuccess: async (_response, { transactionId }) => {
            toast("Updated transaction");
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["transactions"] }),
                invalidateTransactionDetailsQueries(transactionId),
            ]);
        },
        onError: (mutationError) => {
            const hasBackendErrorToast =
                isAxiosError<{ message?: string | string[] }>(mutationError) &&
                Boolean(mutationError.response?.data.message);

            if (!hasBackendErrorToast) {
                error("Failed to update transaction");
            }
        },
    });
}
